/**
 * Google encoded-polyline decoder, ported from the user app's `choose-truck`
 * screen. The algorithm there is plain JavaScript with no React Native
 * dependency, so this is a move rather than a rewrite — the two clients must
 * draw the same line from the same string.
 *
 * Both the authenticated route summary (`polyline`) and the public fare
 * estimate (`routePolyline`) return this format. The estimate widget already
 * receives one and currently discards it.
 *
 * @see https://developers.google.com/maps/documentation/utilities/polylinealgorithm
 */

export interface DecodedPoint {
  lat: number;
  lng: number;
}

export function decodePolyline(encoded: string | null | undefined): DecodedPoint[] {
  if (!encoded) return [];

  const points: DecodedPoint[] = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    let result = 0;
    let shift = 0;
    let byte: number;

    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);

    lat += result & 1 ? ~(result >> 1) : result >> 1;

    result = 0;
    shift = 0;

    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);

    lng += result & 1 ? ~(result >> 1) : result >> 1;

    points.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }

  return points;
}

/** Bounds for fitting a map to a decoded route. Null when there is nothing to fit. */
export function boundsOf(points: DecodedPoint[]): {
  north: number;
  south: number;
  east: number;
  west: number;
} | null {
  if (!points.length) return null;

  return points.reduce(
    (acc, point) => ({
      north: Math.max(acc.north, point.lat),
      south: Math.min(acc.south, point.lat),
      east: Math.max(acc.east, point.lng),
      west: Math.min(acc.west, point.lng),
    }),
    {
      north: points[0].lat,
      south: points[0].lat,
      east: points[0].lng,
      west: points[0].lng,
    },
  );
}
